import { FC, useState } from 'react';
import {
  IconAnchor,
  IconChevronLeft,
  IconChevronRight,
  IconMinusVertical,
  IconMoneybag,
  IconMoon,
  IconNotebook,
  IconPhone,
  IconPlaneArrival,
  IconPlaneDeparture,
  IconSailboat,
  IconScubaMask,
  IconSun,
  IconUser,
} from '@tabler/icons-react';
import GuestDetails from './components/GuestDetail';
import { Divider, useMediaQuery, useTheme, Skeleton } from '@mui/material';
import ThingsToKnowDrawer from './components/ThingsToKnowDrawer';
import ThingsToKnowModal from './components/ThingsToKnowModal';
import TripItineraryDrawer from './components/TripItineraryDrawer';
import TripItineraryModal from './components/TripItineraryModal';
import PaymentDetailsModal from './components/PaymentDetailsModal';
import PaymentDetailsContent from './components/PaymentDetailsContent';
import useTripDetails from '../../hooks/useTripDetailsQuery';
import useGetTripTransfer from '../../hooks/useGetTripTransfer';
import {
  formatDateRange,
  formatTime,
  formatInputDate,
} from '../../common/utilities/dateFormatter';
import useTripInvites from '../../hooks/useTripInvites';
import { TripInvite, UpcomingBookingSummary } from '../../gql/graphql';
import useGetTripBenefits from '../../hooks/useGetTripBenefit';
import useGetBookingRoom from '../../hooks/useGetBookingRoom';
import useGetBoatRooms from '../../hooks/useGetBoatRooms';
import { RoomDetail } from './types';
import { computeNumberOfGuests } from '../../utils/guestCount';
import { computeTripDuration } from '../../utils/tripDuration';
import { getTripManagementStatus } from '../../utils/tripManagementStatus';

const SUPPORT_EMAIL = 'help@santtai.com';

interface TripManagementDetailProps {
  tripId: number;
  booking: UpcomingBookingSummary | undefined;
  onClose: () => void;
}

const TripManagementDetail: FC<TripManagementDetailProps> = ({
  tripId,
  booking,
  onClose,
}) => {
  const [tripItineraryDrawerOpen, setTripItineraryDrawerOpen] = useState(false);
  const [tripItineraryModalOpen, setTripItineraryModalOpen] = useState(false);
  const [paymentDetailsModalOpen, setPaymentDetailsModalOpen] = useState(false);
  const [thingsToKnowDrawerOpen, setThingsToKnowDrawerOpen] = useState(false);
  const [thingsToKnowModalOpen, setThingsToKnowModalOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const tripStatus = getTripManagementStatus(booking?.bookingStatus);

  // Fetch trip details and transfer details using the custom hooks
  const { tripDetails, isLoading: tripDetailLoading } = useTripDetails(
    tripId,
    true,
  );
  const { tripTransfer, tripTransferLoading } = useGetTripTransfer(
    tripId,
    true,
  );

  const { tripBenefits, tripBenefitsLoading } = useGetTripBenefits(tripId);
  const { tripInvites, isLoading: tripInvitesLoading } = useTripInvites(
    { tripId, bookingId: booking?.bookingId },
    true,
  );

  const { boatRooms, boatRoomsLoading } = useGetBoatRooms(
    booking?.boatId ?? 0,
    booking?.tripId ?? 0,
  );

  const { bookingRoom, bookingRoomLoading } = useGetBookingRoom(
    booking?.bookingId ?? 0,
  );

  const tripDuration = computeTripDuration(
    tripDetails?.embarkationDate ?? booking?.embarkationDate,
    tripDetails?.disembarkationDate ?? booking?.disembarkationDate,
  );

  const roomDetails: RoomDetail[] =
    bookingRoom?.bookingRooms?.flatMap(br => {
      const matchingRoomType = boatRooms?.roomTypes.find(
        rt => rt.typeId === br.typeId,
      );
      if (matchingRoomType) {
        const heroImage = matchingRoomType?.roomPhotos?.find(
          photo => photo.isHero,
        )?.imageUrl;

        // Booking lines store guest spaces in numberOfRooms (per-guest pricing).
        return Array.from({ length: br.numberOfRooms }, () => ({
          imageUrl: heroImage || '',
          typeName: matchingRoomType.typeName,
          pricePerRoom: br.pricePerRoom,
        }));
      }
      return [];
    }) || [];

  const handleOpenTripItinerary = () => {
    if (isMobile) {
      setTripItineraryDrawerOpen(true);
    } else {
      setTripItineraryModalOpen(true);
    }
  };

  const handleOpenPaymentDetails = () => {
    if (isMobile) {
      setPaymentDetailsModalOpen(true);
    }
  };

  const handleOpenThingsToKnow = () => {
    if (isMobile) {
      setThingsToKnowDrawerOpen(true);
    } else {
      setThingsToKnowModalOpen(true);
    }
  };

  const handleContactBoat = () => {
    const boatName = booking?.boatName || tripDetails?.boatName || 'boat';
    const subject = encodeURIComponent(`Question about ${boatName}`);
    window.open(`mailto:${SUPPORT_EMAIL}?subject=${subject}`, '_blank');
  };

  const calculateTotalPrice = (details: RoomDetail[]) => {
    return details.reduce((total, room) => total + room.pricePerRoom, 0);
  };

  // ── BUSINESS LOGIC: Filter guest invites based on the magic query string ──
  //
  // 1. Retrieve the "magic" token from the URL.
  // 2. If the magic token exists and the booking is NOT for the lead guest (booking.isLeadGuest === false),
  //    then filter the list of trip invites so that only the invite matching the magic token is shown.
  //
  // (Assumes that each TripInvite has a "magicToken" property.)
  const searchParams = new URLSearchParams(window.location.search);
  const magicToken = searchParams.get('magic');

  const filteredTripInvites =
    magicToken && booking && !booking.isLeadGuest
      ? (tripInvites as TripInvite[]).filter(
          invite => invite.magicLink === magicToken,
        )
      : (tripInvites as TripInvite[]);

  const renderTripDetail = () => (
    <>
      <div className="flex items-start gap-4 self-stretch">
        <div className="flex flex-col items-start gap-2">
          {tripDetailLoading ? (
            <Skeleton width={120} height={30} />
          ) : (
            <span className="font-semibold text-neutral-800">
              {formatDateRange(
                tripDetails?.embarkationDate,
                tripDetails?.disembarkationDate,
                true,
              )}
            </span>
          )}
          <div className="flex flex-start text-sm text-neutral-500">
            {tripDetailLoading ? (
              <Skeleton width={60} height={20} />
            ) : (
              <>
                <div className="flex items-center gap-1">
                  <IconSun size={20} />
                  <span>{tripDuration ? `${tripDuration.days}D` : 'N/A'}</span>
                </div>
                <div className="flex items-center flex-start mx-1 text-neutral-100">
                  <IconMinusVertical size={20} />
                </div>
                <div className="flex items-center gap-1">
                  <IconMoon size={20} />
                  <span>
                    {tripDuration ? `${tripDuration.nights}N` : 'N/A'}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col items-start gap-2">
          {tripDetailLoading ? (
            <Skeleton width={150} height={30} />
          ) : (
            <span className="font-semibold text-neutral-800">
              {booking?.routeName || ''}
            </span>
          )}
          <div className="flex flex-start text-sm text-neutral-500">
            {tripDetailLoading ? (
              <Skeleton width={60} height={20} />
            ) : (
              <>
                <div className="flex items-center gap-1">
                  <IconScubaMask size={20} />
                  <span>{booking?.diveCount || '0'} dives</span>
                </div>
                <div className="flex items-center flex-start mx-1 text-neutral-100">
                  <IconMinusVertical size={20} />
                </div>
                <div className="flex items-center gap-1">
                  <IconUser size={20} />
                  <span>{booking?.guestCount || '0'} guests</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-start gap-4 self-stretch mt-4">
        <div className="flex flex-col items-start gap-2">
          <div className="flex items-start gap-2">
            <IconPlaneArrival size={20} />
            <span className="font-semibold text-neutral-800">Arrival</span>
          </div>
          <div className="flex flex-col content-center items-start gap-2 text-neutral-500 text-sm">
            {tripTransferLoading ? (
              <>
                <Skeleton width={120} height={20} />
                <Skeleton width={60} height={20} />
                <Skeleton width={60} height={20} />
              </>
            ) : (
              <>
                <span>
                  {tripTransfer?.tripTransfer.departureAirportName || ''} (
                  {tripTransfer?.tripTransfer.departureAirportCode || ''})
                </span>
                <span>
                  {formatInputDate(tripDetails?.embarkationDate) || 'N/A'}
                </span>
                <span>
                  {formatTime(tripTransfer?.tripTransfer.embarkationTime) || ''}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col items-start gap-2">
          <div className="flex items-start gap-2">
            <IconPlaneDeparture size={20} />
            <span className="font-semibold text-neutral-800">Departure</span>
          </div>
          <div className="flex flex-col content-center items-start gap-2 text-neutral-500 text-sm">
            {tripTransferLoading ? (
              <>
                <Skeleton width={120} height={20} />
                <Skeleton width={60} height={20} />
                <Skeleton width={60} height={20} />
              </>
            ) : (
              <>
                <span>
                  {tripTransfer?.tripTransfer.returnAirportName || ''} (
                  {tripTransfer?.tripTransfer.returnAirportCode || 'MLE'})
                </span>
                <span>
                  {formatInputDate(tripDetails?.disembarkationDate) || 'N/A'}
                </span>
                <span>
                  {formatTime(tripTransfer?.tripTransfer.disembarkationTime) ||
                    ''}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );

  const PaymentDetails = () => (
    <div>
      <span className="text-neutral-800 text-xl font-semibold">
        Payment details
      </span>
      {tripBenefitsLoading && boatRoomsLoading && bookingRoomLoading ? (
        <Skeleton width="100%" height={200} />
      ) : (
        <PaymentDetailsContent
          tripBenefits={tripBenefits?.tripBenefits}
          roomDetails={roomDetails}
        />
      )}
    </div>
  );

  const totalGuestSlotsFromRooms = computeNumberOfGuests(
    bookingRoom?.bookingRooms?.map(item => ({
      numberOfRooms: item.numberOfRooms,
    })) ?? [],
  );
  const numberOfGuests =
    booking?.guestCount && booking.guestCount > 0
      ? booking.guestCount
      : totalGuestSlotsFromRooms;

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-6 h-full overflow-hidden">
      <div
        className="col-span-1 md:col-span-3 p-6 flex flex-col gap-6 md:px-36 overflow-y-auto"
        style={{
          scrollbarWidth: 'none' /* Firefox */,
          msOverflowStyle: 'none' /* IE and Edge */,
        }}
      >
        <style>
          {`
            /* Chrome, Edge, and Safari */
            .col-span-1::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>
        <button
          type="button"
          className="flex items-center cursor-pointer"
          onClick={onClose}
          aria-label="Back to trips"
        >
          <IconChevronLeft size={24} />
          <span className="hidden md:block font-semibold ml-2 text-neutral-800">
            Trips
          </span>
        </button>
        <div className="flex flex-col items-start gap-6">
          <div className="flex flex-col items-start gap-6 self-stretch">
            <div className="flex items-start gap-4 self-stretch">
              <img
                className="w-[3.75rem] h-[3.75rem] rounded-lg md:w-[96px] md:h-[96px]"
                src={booking?.boatPhoto}
                alt="boat image"
              />
              <div className="flex flex-col">
                <div className="flex flex-col items-start content-center gap-2 flex-wrap md:flex-row md:items-center">
                  <span className="text-xl font-semibold text-neutral-800 md:text-2xl">
                    {tripDetailLoading ? (
                      <Skeleton width={100} />
                    ) : (
                      tripDetails?.boatName || 'Boat name'
                    )}
                  </span>
                  <div
                    className={`flex py-1 px-2 items-start gap-2 rounded ml-2 md:ml-4 ${tripStatus.className}`}
                  >
                    <span className="text-sm font-semibold">
                      {tripStatus.label}
                    </span>
                  </div>
                </div>
                <div className="hidden md:block mt-4">{renderTripDetail()}</div>
              </div>
            </div>
          </div>
          <div className="md:hidden">{renderTripDetail()}</div>
        </div>
        {tripInvitesLoading && bookingRoomLoading ? (
          <Skeleton width="100%" height={200} />
        ) : (
          <GuestDetails
            guests={filteredTripInvites}
            numberOfGuests={numberOfGuests}
            bookingId={booking?.bookingId || 0}
            tripId={booking?.tripId || 0}
            isLeadGuest={booking?.isLeadGuest || false}
          />
        )}
        <div className="w-full">
          <span className="text-neutral-800 text-xl font-semibold">
            Additional details
          </span>
          <button
            type="button"
            className="flex justify-between items-center w-full mt-4"
            onClick={handleOpenTripItinerary}
          >
            <div className="flex">
              <IconAnchor size={20} />
              <div className="flex items-start flex-col ml-4">
                <span className="text-neutral-800 font-semibold">
                  Check trip itinerary
                </span>
                <span className="text-neutral-500 text-sm">
                  View your trip details
                </span>
              </div>
            </div>
            <IconChevronRight size={20} />
          </button>
          <div className="mt-4">
            <Divider />
          </div>
        </div>
        <div className="w-full">
          <button
            type="button"
            className="flex justify-between items-center w-full"
            onClick={() =>
              window.open(
                `https://www.santtai.com/boat/${booking?.boatSlug}`,
                '_blank',
              )
            }
          >
            <div className="flex">
              <IconSailboat size={20} />
              <div className="flex items-start flex-col ml-4">
                <span className="text-neutral-800 font-semibold">
                  Liveaboard boat
                </span>
                <span className="text-neutral-500 text-sm">
                  {booking?.boatName || 'Boat name'}
                </span>
              </div>
            </div>
            <IconChevronRight size={20} />
          </button>
          <div className="mt-4">
            <Divider />
          </div>
        </div>
        <div className="w-full">
          <button
            type="button"
            className="flex justify-between items-center w-full"
            onClick={handleContactBoat}
          >
            <div className="flex">
              <IconPhone size={20} />
              <div className="flex items-start flex-col ml-4">
                <span className="text-neutral-800 font-semibold">
                  Contact boat
                </span>
                <span className="text-neutral-500 text-sm">
                  {SUPPORT_EMAIL}
                </span>
              </div>
            </div>
            <IconChevronRight size={20} />
          </button>
          <div className="mt-4">
            <Divider />
          </div>
        </div>
        <div className="w-full md:hidden">
          <button
            type="button"
            className="flex justify-between items-center w-full"
            onClick={handleOpenPaymentDetails}
          >
            <div className="flex">
              <IconMoneybag size={20} />
              <div className="flex items-start flex-col ml-4">
                <span className="text-neutral-800 font-semibold">
                  Payment details
                </span>
                <span className="text-neutral-500 text-sm">
                  B{calculateTotalPrice(roomDetails).toLocaleString()}
                </span>
              </div>
            </div>
            <IconChevronRight size={20} />
          </button>
          <div className="mt-4">
            <Divider />
          </div>
        </div>
        <div className="w-full">
          <button
            type="button"
            className="flex justify-between items-center w-full"
            onClick={handleOpenThingsToKnow}
          >
            <div className="flex">
              <IconNotebook size={20} />
              <div className="flex items-start flex-col ml-4">
                <span className="text-neutral-800 font-semibold">
                  Things to know
                </span>
                <span className="text-neutral-500 text-sm">
                  Visa, gear, and boarding tips
                </span>
              </div>
            </div>
            <IconChevronRight size={20} />
          </button>
        </div>

        <TripItineraryDrawer
          open={tripItineraryDrawerOpen}
          onClose={() => setTripItineraryDrawerOpen(false)}
          tripId={tripId}
        />
        <PaymentDetailsModal
          open={paymentDetailsModalOpen}
          onClose={() => setPaymentDetailsModalOpen(false)}
          tripBenefits={tripBenefits?.tripBenefits}
          roomDetails={roomDetails}
        />
        <ThingsToKnowDrawer
          open={thingsToKnowDrawerOpen}
          onClose={() => setThingsToKnowDrawerOpen(false)}
        />
        <TripItineraryModal
          open={tripItineraryModalOpen}
          onClose={() => setTripItineraryModalOpen(false)}
          tripId={tripId}
        />
        <ThingsToKnowModal
          open={thingsToKnowModalOpen}
          onClose={() => setThingsToKnowModalOpen(false)}
        />
      </div>
      <div className="hidden md:block col-span-2">
        <div className="sticky top-6 pr-36">
          <PaymentDetails />
        </div>
      </div>
    </div>
  );
};

export default TripManagementDetail;
